import {AfterViewInit, Component, ElementRef, Input, NgZone, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {FabricRepository} from '../../../../repositories/fabric.repository';
import {PatternRepository} from '../../../../repositories/pattern.repository';
import SewingPatternInterface from '../../../../models/sewing-pattern.interface';
import FabricInterface from '../../../../models/fabric.interface';
import MaterialInterface from '../../../../models/material.interface';
import PatternAreaInterface from '../../../../models/pattern-area.interface';
import {Router} from '@angular/router';
import {Storage} from '@ionic/storage';
import rgb2hex from 'rgb2hex';
import {PatternTransformService} from '../../../../services/pattern/pattern-transform.service';
import SewingPatternProject from '../../../../models/sewing-pattern-project.model';
import {ConfiguratorService} from '../../../../services/configurator/configurator.service';
import {DomSanitizer, SafeUrl} from "@angular/platform-browser";

@Component({
    selector: 'app-image-mask',
    templateUrl: './image-mask.component.html',
    styleUrls: ['./image-mask.component.css'],
    encapsulation: ViewEncapsulation.None
})
export class ImageMaskComponent implements OnInit, AfterViewInit {
    @ViewChild('svgContainer') svgContainer: ElementRef<HTMLElement>;
    @ViewChild('definitions') definitions: ElementRef<HTMLElement>;

    @Input() selectedPattern: SewingPatternInterface = null;
    @Input() selectedProject: SewingPatternProject = null;
    @Input() edit: string = null;

    private isSaving = false;

    public sewingPatternSVGImage: SafeUrl;

    public patterns: SewingPatternInterface[];
    public allFabrics: FabricInterface[];

    public areaFillings: PatternAreaInterface[] = [];

    public selectedArea: PatternAreaInterface = null;
    public selectedMaterial: MaterialInterface = null;
    public selectedVariations: { id: string, name: string }[] = null;

    get selectedVariationIds() {
        return this.selectedVariations.map(variation => variation.id);
    }

    get availableMaterials() {
        return (this.selectedArea?.materials ?? this.fabricRepository.materials())
            .sort((a, b) => a.position - b.position);
    }

    get fabrics() {
        if (!this.selectedMaterial) {
            return [];
        }

        const {fabrics} = this.selectedMaterial;

        if (this.selectedVariations.length > 0) {
            const variationIds = this.selectedVariations.map(variation => variation.id);
            return fabrics
                .filter(fabric => variationIds.includes(fabric.variation))
                .sort((a, b) => a.position - b.position);
        }

        return fabrics
            .sort((a, b) => a.position - b.position);
    }

    get selectedFabrics() {
        const selectedFabrics = this.selectedPattern.areas
            .map(area => {
                return area.fabric;
            })
            .filter(fabric => !!fabric);

        if (this.selectedArea && this.selectedArea.fabric) {
            selectedFabrics.push(this.selectedArea.fabric);
        }

        return selectedFabrics;
    }

    constructor(private fabricRepository: FabricRepository,
                private patternRepository: PatternRepository,
                private patternTransformService: PatternTransformService,
                private configuratorService: ConfiguratorService,
                private zone: NgZone,
                private router: Router,
                private domSanitizer: DomSanitizer) {
    }

    ngOnInit() {
        this.allFabrics = this.fabricRepository.all();
        this.patterns = this.patternRepository.all();
    }

    async ngAfterViewInit() {
        if (this.selectedProject) {
            this.selectedPattern = this.selectedProject.sewingPattern;
        }

        this.setSewingPatterns();

        this.selectedArea = this.selectedPattern.areas[0];
        this.selectMaterial(this.availableMaterials[0]);
    }

    selectArea(area: PatternAreaInterface) {
        this.selectedArea = area;
        this.selectMaterial(this.availableMaterials[0]);

        const groups = this.svgContainer.nativeElement.querySelectorAll('[data-area]');
        groups.forEach((group: HTMLElement) => {
            if (group.dataset.area === this.selectedArea.id) {
                const previousFill = group.style.fill;
                group.style.setProperty('fill', `rgba(255, 140, 140, 1)`);

                setTimeout(() => {
                    group.style.setProperty('fill', previousFill);
                }, 400);
            }
        });
    }

    selectMaterial(material: MaterialInterface) {
        this.selectedMaterial = material;
        this.selectedVariations = [];
    }

    selectVariation(variation: { id: string, name: string }) {
        if (this.selectedVariations.find(selectedVariation => selectedVariation.id === variation.id)) {
            this.selectedVariations = this.selectedVariations.filter(selectedVariation => selectedVariation.id !== variation.id);
        } else {
            this.selectedVariations.push(variation);
        }
    }

    async setSewingPatterns() {
        this.svgContainer.nativeElement.innerHTML = await this.patternTransformService.generateFinalSewingPatternSVG(this.selectedPattern, false);

        setTimeout(() => {
            this.prepareGroups();
        }, 300);
    }

    prepareGroups() {
        const {areas} = this.selectedPattern;

        this.patternTransformService.prepareSvgGroups(this.svgContainer.nativeElement, areas, (group, area) => {
            group.addEventListener('click', ev => {
                const target = ev.currentTarget as HTMLElement;
                const areaId = target.dataset.area;

                const areaToSelect = this.selectedPattern.areas.find(availableArea => availableArea.id === areaId);
                this.selectArea(areaToSelect);
            });
        });
    }

    selectFilling(fabric) {
        this.selectedArea.fabric = fabric;

        this.setSewingPatterns();
    }

    async generateTemporarySewingPatternProject(sewingPattern) {
        const patternProject = await this.configuratorService.savePersonalSewingPattern(
            'Preview',
            sewingPattern,
            await this.patternTransformService.generateFinalSewingPatternSVG(sewingPattern, true, `${Date.now()}`),
            'preview',
            false
        );
        await this.configuratorService.saveAsPng(patternProject);
        patternProject.url = await this.configuratorService.loadSanitizedPatternProjectUrl(patternProject);

        return patternProject;
    }

    async saveSewingPatternProject() {
        if (this.isSaving) {
            return;
        }
        this.isSaving = true;

        this.patternTransformService.currentPatternIsTemporary = !this.edit;
        this.patternTransformService.temporaryPatternProject = await this.generateTemporarySewingPatternProject(this.selectedPattern);

        if (this.selectedProject) {
            this.patternTransformService.currentPatternProject = this.selectedProject;
        }

        this.router.navigate(['configuration/save'], {
            replaceUrl: true
        });

        this.isSaving = false;
    }
}
